import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Row, Col, Card, Statistic, List, message, Button, Popconfirm, Tag } from 'antd';
import { FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import screenfull from 'screenfull';
import api from '../api';

const AttendanceSessionPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [stats, setStats] = useState({ signedStudents: [], unsignedStudents: [] });
  const [qrCodeValue, setQrCodeValue] = useState('loading...');
  const [countdown, setCountdown] = useState(10);
  const [endCountdown, setEndCountdown] = useState(null); // 定时结束倒计时（秒）
  const [isFullscreen, setIsFullscreen] = useState(false);
  const intervalRef = useRef(null);
  const containerRef = useRef(null);

  const stopInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const fetchSessionData = useCallback(async () => {
    try {
      const response = await api.get(`/attendance/sessions/${id}`);
      setSession(response.data);
      setStats(response.data.stats); // Assuming stats are nested under session data
      
      // 计算定时结束倒计时
      const sessionData = response.data.session || response.data;
      if (sessionData.durationMinutes && sessionData.durationMinutes > 0 && sessionData.startedAt) {
        const startTime = new Date(sessionData.startedAt).getTime();
        const endTime = startTime + sessionData.durationMinutes * 60 * 1000;
        const remaining = Math.max(0, Math.floor((endTime - Date.now()) / 1000));
        setEndCountdown(remaining);
      } else {
        setEndCountdown(null);
      }
      
      if(response.data.status === 'ended') {
        stopInterval();
      }
    } catch (error) {
      message.error('获取签到详情失败');
      stopInterval();
    }
  }, [id, stopInterval]);

  const fetchQrCode = useCallback(async () => {
    try {
        const response = await api.get(`/attendance/sessions/${id}/qrcode`);
        setQrCodeValue(response.data.token); // The token for the QR code
        
        // 基于服务器时间计算剩余秒数，实现多端同步
        const serverTime = response.data.serverTime || Date.now();
        const interval = 10; // 10秒刷新周期
        const elapsed = Math.floor((serverTime / 1000) % interval);
        const remaining = interval - elapsed;
        setCountdown(remaining > 0 ? remaining : interval);
    } catch (error) {
        // Don't show error message for QR code fetch, as it might be frequent
        console.error("无法获取二维码: ", error);
        if(session?.status !== 'ended') setQrCodeValue('error');
        else setQrCodeValue('ended');
    }
  }, [id, session?.status]);

  const startInterval = useCallback(() => {
    stopInterval(); // Ensure no multiple intervals running
    fetchSessionData();
    fetchQrCode();
    let tick = 0;
    intervalRef.current = setInterval(() => {
        tick++;
        // 每3秒刷新签到数据
        if (tick % 3 === 0) {
            fetchSessionData();
        }
        setCountdown(prev => {
            if (prev <= 1) {
                fetchQrCode(); // Fetch new QR code every 10 seconds
                return 10;
            }
            return prev - 1;
        });
        // 更新定时结束倒计时
        setEndCountdown(prev => {
            if (prev === null) return null;
            if (prev <= 0) return 0;
            return prev - 1;
        });
    }, 1000);
  }, [stopInterval, fetchSessionData, fetchQrCode]);

  useEffect(() => {
    startInterval();
    return () => stopInterval();
  }, [startInterval, stopInterval]);

  // 全屏功能
  const toggleFullscreen = () => {
    if (screenfull.isEnabled && containerRef.current) {
      screenfull.toggle(containerRef.current);
    }
  };

  useEffect(() => {
    if (screenfull.isEnabled) {
      const handleChange = () => setIsFullscreen(screenfull.isFullscreen);
      screenfull.on('change', handleChange);
      return () => screenfull.off('change', handleChange);
    }
  }, []);

  const handleEndSession = async () => {
    try {
      await api.post(`/attendance/end/${id}`);
      message.success("签到已结束");
      setQrCodeValue('ended');
      stopInterval();
      fetchSessionData();
      navigate('/attendance');
    } catch (error) {
      message.error("结束签到失败");
    }
  };
  
  const handleManualSign = async (userId) => {
    try {
        await api.post(`/attendance/manual-sign`, { userId, sessionId: id });
        message.success("补签成功");
        fetchSessionData(); // Refresh data to show the change
    } catch (error) {
        message.error("补签失败");
    }
  };

  // 格式化倒计时显示
  const formatEndCountdown = (seconds) => {
    if (seconds === null || seconds === undefined) return null;
    if (seconds <= 0) return '即将结束';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}分${secs.toString().padStart(2, '0')}秒`;
    }
    return `${secs}秒`;
  };

  if (!session) return <p>加载中...</p>;

  const getStatusTag = (status) => {
      if(status === 'active') return <Tag color="green">进行中</Tag>;
      if(status === 'ended') return <Tag color="red">已结束</Tag>;
      return <Tag>{status}</Tag>
  }

  // 计算二维码尺寸 - 全屏时更大
  const qrSize = isFullscreen ? 360 : 256;

  // 签到已结束 - 显示总结页面
  if (session.status === 'ended') {
    const signedCount = stats.signedCount || stats.signedStudents?.length || 0;
    const totalCount = session.snapshotClassSize || 0;
    const unsignedCount = totalCount - signedCount;
    const signRate = totalCount > 0 ? (signedCount / totalCount * 100).toFixed(1) : 0;
    
    return (
      <div style={{ padding: 24 }}>
        {/* 签到结束标题 */}
        <Card style={{ marginBottom: 24, textAlign: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
          <div style={{ color: '#fff', padding: '24px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>✓</div>
            <div style={{ fontSize: 28, fontWeight: 'bold', marginBottom: 8 }}>签到已结束</div>
            <div style={{ fontSize: 16, opacity: 0.9 }}>{session.class?.name || '未知班级'}</div>
          </div>
        </Card>

        {/* 统计数据 */}
        <Row gutter={24} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic 
                title="应签人数" 
                value={totalCount} 
                valueStyle={{ color: '#1890ff', fontSize: 36 }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic 
                title="已签到" 
                value={signedCount} 
                valueStyle={{ color: '#52c41a', fontSize: 36 }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic 
                title="未签到" 
                value={unsignedCount} 
                valueStyle={{ color: '#ff4d4f', fontSize: 36 }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic 
                title="签到率" 
                value={signRate} 
                suffix="%" 
                valueStyle={{ color: signRate >= 80 ? '#52c41a' : signRate >= 60 ? '#faad14' : '#ff4d4f', fontSize: 36 }}
              />
            </Card>
          </Col>
        </Row>

        {/* 签到时间信息 */}
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={24}>
            <Col span={8}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#999', marginBottom: 8 }}>开始时间</div>
                <div style={{ fontSize: 18, fontWeight: 500 }}>
                  {session.session?.startedAt ? new Date(session.session.startedAt).toLocaleString() : '-'}
                </div>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#999', marginBottom: 8 }}>结束时间</div>
                <div style={{ fontSize: 18, fontWeight: 500 }}>
                  {session.session?.endedAt ? new Date(session.session.endedAt).toLocaleString() : '-'}
                </div>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#999', marginBottom: 8 }}>持续时长</div>
                <div style={{ fontSize: 18, fontWeight: 500 }}>
                  {session.session?.startedAt && session.session?.endedAt ? 
                    (() => {
                      const duration = new Date(session.session.endedAt) - new Date(session.session.startedAt);
                      const minutes = Math.floor(duration / 60000);
                      const seconds = Math.floor((duration % 60000) / 1000);
                      return `${minutes}分${seconds}秒`;
                    })() : '-'}
                </div>
              </div>
            </Col>
          </Row>
        </Card>

        {/* 签到明细 */}
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Card 
              title={<span style={{ color: '#52c41a' }}>✓ 已签到 ({stats.signedStudents?.length || 0})</span>}
              styles={{ body: { maxHeight: 400, overflow: 'auto' } }}
            >
              <List
                dataSource={stats.signedStudents || []}
                renderItem={item => (
                  <List.Item>
                    <List.Item.Meta 
                      title={item.user?.name || '未知'}
                      description={`签到时间: ${item.signedAt ? new Date(item.signedAt).toLocaleTimeString() : '-'}`}
                    />
                    <Tag color={item.method === 'scan' ? 'blue' : 'orange'}>
                      {item.method === 'scan' ? '扫码' : '补签'}
                    </Tag>
                  </List.Item>
                )}
                locale={{ emptyText: '无签到记录' }}
              />
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card 
              title={<span style={{ color: '#ff4d4f' }}>✗ 未签到 ({stats.unsignedStudents?.length || 0})</span>}
              styles={{ body: { maxHeight: 400, overflow: 'auto' } }}
            >
              <List
                dataSource={stats.unsignedStudents || []}
                renderItem={item => (
                  <List.Item>
                    <List.Item.Meta title={item.user?.name || '未知'} />
                    {item.tag && (
                      <Tag color={item.tag === 'LEAVE' ? 'orange' : item.tag === 'ABSENT' ? 'red' : 'default'}>
                        {item.tag === 'LEAVE' ? '请假' : item.tag === 'ABSENT' ? '旷课' : item.tag}
                      </Tag>
                    )}
                  </List.Item>
                )}
                locale={{ emptyText: '全员已签到' }}
              />
            </Card>
          </Col>
        </Row>

        {/* 返回按钮 */}
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <Button type="primary" size="large" onClick={() => navigate('/attendance')}>
            返回签到管理
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef} 
      style={{ 
        padding: isFullscreen ? 32 : 0, 
        background: isFullscreen ? '#f0f2f5' : 'transparent',
        minHeight: isFullscreen ? '100vh' : 'auto',
        overflow: 'auto'
      }}
    >
      {/* 定时结束倒计时 - 顶部显示 */}
      {endCountdown !== null && (
        <div style={{ 
          marginBottom: 16, 
          padding: '16px 24px', 
          background: endCountdown <= 60 ? 'linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)' : 
                      endCountdown <= 180 ? 'linear-gradient(135deg, #faad14 0%, #ffc53d 100%)' : 
                      'linear-gradient(135deg, #1890ff 0%, #69c0ff 100%)', 
          borderRadius: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          <div style={{ color: '#fff', fontSize: 16, marginRight: 16 }}>⏱️ 定时结束倒计时</div>
          <div style={{ 
            fontSize: isFullscreen ? 36 : 32, 
            fontWeight: 'bold', 
            color: '#fff'
          }}>
            {formatEndCountdown(endCountdown)}
          </div>
        </div>
      )}

      {/* 全屏按钮 */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button 
          icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
          onClick={toggleFullscreen}
        >
          {isFullscreen ? '退出全屏' : '全屏展示'}
        </Button>
      </div>

      <Row gutter={24}>
        <Col xs={24} lg={8}>
          <Card title={`动态签到码 (${countdown}s)`}>
            <div style={{ textAlign: 'center'}}>
              {qrCodeValue !== 'loading...' && qrCodeValue !== 'error' && qrCodeValue !== 'ended' ? 
                  <QRCodeSVG value={qrCodeValue} size={qrSize} /> : 
                  <div style={{width: qrSize, height: qrSize, background: '#f0f2f5', display:'flex', alignItems:'center', justifyContent:'center', margin: '0 auto'}}>{qrCodeValue}</div>
              }
              <p style={{ marginTop: 16, fontSize: isFullscreen ? 18 : 14 }}>请学员使用微信小程序扫码签到</p>
              <p style={{ color: countdown <=3 && countdown > 0 ? 'red': 'inherit', fontSize: isFullscreen ? 16 : 14 }}>（二维码每10秒刷新一次）</p>
              
              <Popconfirm
                  title="确定要结束本次签到吗？"
                  onConfirm={handleEndSession}
                  okText="确定"
                  cancelText="取消"
                  disabled={session.status === 'ended'}
              >
                  <Button type="primary" danger disabled={session.status === 'ended'} size={isFullscreen ? 'large' : 'middle'}>
                      结束签到
                  </Button>
              </Popconfirm>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={16}>
          <Row gutter={16}>
              <Col span={6}><Statistic title="班级" value={session.class.name} valueStyle={{ fontSize: isFullscreen ? 28 : 24 }} /></Col>
              <Col span={6}><Statistic title="状态" value={session.status} formatter={(val) => getStatusTag(val)} /></Col>
              <Col span={6}><Statistic title="已签/总数" value={`${stats.signedCount || 0}/${session.snapshotClassSize}`} valueStyle={{ fontSize: isFullscreen ? 28 : 24 }} /></Col>
              <Col span={6}><Statistic title="签到率" value={session.snapshotClassSize > 0 ? ((stats.signedCount || 0) / session.snapshotClassSize * 100).toFixed(1) : 0} suffix="%" valueStyle={{ fontSize: isFullscreen ? 28 : 24 }} /></Col>
          </Row>
          <Row gutter={16} style={{marginTop: 24}}>
              <Col xs={24} md={12}>
                  <Card 
                    title={`已签到 (${stats.signedStudents.length})`}
                    styles={{ body: { maxHeight: isFullscreen ? 'calc(100vh - 280px)' : 400, overflow: 'auto' } }}
                  >
                      <List
                          dataSource={stats.signedStudents || []}
                          renderItem={item => (
                              <List.Item>
                                  <List.Item.Meta 
                                      title={<span style={{ fontSize: isFullscreen ? 16 : 14 }}>{item.user.name}</span>}
                                      description={`签到时间: ${new Date(item.signedAt).toLocaleTimeString()}`}
                                  />
                                  <Tag color={item.method === 'scan' ? 'blue' : 'orange'}>{item.method === 'scan' ? '扫码' : '补签'}</Tag>
                              </List.Item>
                          )}
                      />
                  </Card>
              </Col>
              <Col xs={24} md={12}>
                  <Card 
                    title={`未签到 (${stats.unsignedStudents.length})`}
                    styles={{ body: { maxHeight: isFullscreen ? 'calc(100vh - 280px)' : 400, overflow: 'auto' } }}
                  >
                      <List
                          dataSource={stats.unsignedStudents || []}
                          renderItem={item => (
                              <List.Item actions={[
                                  <Button type="link" disabled={session.status === 'ended'} onClick={() => handleManualSign(item.user.id)}>补签</Button>
                              ]}>
                                  <List.Item.Meta title={<span style={{ fontSize: isFullscreen ? 16 : 14 }}>{item.user.name}</span>} />
                              </List.Item>
                          )}
                      />
                  </Card>
              </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default AttendanceSessionPage;
